/**
 * The Ledger page's pure parts: view and kind labels, grouping (by when it
 * is due, or by person), and how a due date reads against today. Dates are YYYY-MM-DD days, and
 * "today" comes from the Worker (the owner's time zone), so nothing here
 * depends on the browser's clock or zone.
 */

import type {
  ItemKind,
  ItemStatus,
  LedgerItem,
  LedgerView,
  SuggestionFit
} from '@satisa-memory/shared'

export const VIEW_LABELS: Record<LedgerView, string> = {
  all: 'All',
  i_owe: 'I owe',
  waiting: 'Owed to me',
  follow_up: 'Follow-up',
  decisions: 'Decisions',
  closed: 'Closed',
  trash: 'Trash'
}

export const KIND_LABELS: Record<ItemKind, string> = {
  i_owe: 'I owe',
  owed_to_me: 'Owed to me',
  follow_up: 'Follow-up',
  decision: 'Decision'
}

/**
 * A suggested decision, by how it bears on the to-do: the banner's question
 * and answers, and the list row's mark. Accepting a moot one drops the to-do.
 */
export const SUGGESTION_WORDS: Record<
  SuggestionFit,
  {
    lead: string
    accept: string
    acceptTitle: string
    dismiss: string
    dismissTitle: string
    mark: string
  }
> = {
  settles: {
    lead: 'Settled by',
    accept: 'Yes, decided',
    acceptTitle: 'Done, decided by it',
    dismiss: 'No',
    dismissTitle: 'Not settled by it',
    mark: 'decided?'
  },
  partly: {
    lead: 'Partly settled by',
    accept: 'Close it, decided',
    acceptTitle: 'Done, decided by it: what is left is not worth a to-do',
    dismiss: 'Keep open',
    dismissTitle: 'What is left still needs doing',
    mark: 'partly decided?'
  },
  moot: {
    lead: 'Made moot by',
    accept: 'Drop it',
    acceptTitle: 'Dropped: the decision makes it unnecessary',
    dismiss: 'Still needed',
    dismissTitle: 'Still needed despite it',
    mark: 'moot?'
  }
}

/** The item kind each kind view lists (KindMark.vue draws its icon). */
export const VIEW_KINDS: Partial<Record<LedgerView, ItemKind>> = {
  i_owe: 'i_owe',
  waiting: 'owed_to_me',
  follow_up: 'follow_up',
  decisions: 'decision'
}

export interface ItemGroup {
  key: string
  name: string
  items: LedgerItem[]
  /** The rows name their person: the group is not one person's. */
  withPerson: boolean
}

const NOBODY = 'No one'

/**
 * One group per counterpart, in the order their most pressing item comes:
 * the list arrives sorted by due date, so first appearance is that order.
 * Items with no counterpart go last.
 */
export function groupByPerson(items: LedgerItem[]): ItemGroup[] {
  const groups = new Map<string, ItemGroup>()
  for (const item of items) {
    const key = item.person?.id ?? ''
    let g = groups.get(key)
    if (!g) {
      g = { key, name: item.person?.name ?? NOBODY, items: [], withPerson: false }
      groups.set(key, g)
    }
    g.items.push(item)
  }
  const nobody = groups.get('')
  groups.delete('')
  return [...groups.values(), ...(nobody ? [nobody] : [])]
}

const DUE_GROUPS = [
  { key: 'overdue', name: 'Overdue' },
  { key: 'week', name: 'Due this week' },
  { key: 'later', name: 'Due later' },
  { key: 'undated', name: 'No due date' }
] as const

/**
 * The open to-dos by priority: overdue, due in the next seven days (today
 * included), later, and undated, each in the Worker's order (soonest due
 * first, the newest undated first). Empty groups are left out.
 */
export function groupByDue(items: LedgerItem[], today: string): ItemGroup[] {
  const groups: ItemGroup[] = DUE_GROUPS.map((g) => ({ ...g, items: [], withPerson: true }))
  for (const item of items) {
    const diff = item.dueDate ? dayNumber(item.dueDate) - dayNumber(today) : null
    groups[diff === null ? 3 : diff < 0 ? 0 : diff < 7 ? 1 : 2]!.items.push(item)
  }
  return groups.filter((g) => g.items.length)
}

/**
 * How a view lays out its items. The to-do views (all, I owe, owed to me, follow-up)
 * come by when they are due; decisions by date, newest first, as the Worker
 * sends them: one list with no heading. In these the person is only a
 * filter. Closed and trashed items group by person.
 */
export function groupsFor(view: LedgerView, items: LedgerItem[], today: string): ItemGroup[] {
  if (view === 'decisions') {
    return items.length ? [{ key: 'by-date', name: '', items, withPerson: true }] : []
  }
  if (view === 'closed' || view === 'trash') return groupByPerson(items)
  return groupByDue(items, today)
}

/**
 * Whether a changed item leaves the view on screen at once: whenever the view
 * no longer lists it. A to-do marked done leaves All, a reopened one leaves
 * Closed, a restored one leaves Trash, a re-kinded one leaves its kind's view.
 */
export function leavesView(view: LedgerView, item: LedgerItem): boolean {
  return viewFor(view, item) !== view
}

/**
 * The view that lists an item: the one on screen when it holds the item's
 * kind, else the one that does. All holds open to-dos only, so an open
 * decision is in Decisions, a closed item in Closed, a trashed one in Trash.
 */
export function viewFor(view: LedgerView, item: LedgerItem): LedgerView {
  if (item.trashedAt) return 'trash'
  if (item.status !== 'open') return 'closed'
  if (item.kind === 'decision') return 'decisions'
  return view === 'all' || VIEW_KINDS[view] === item.kind ? view : 'all'
}

/** Everyone an item is with: the counterpart first, then the others on an own note. */
export function withWhom(item: LedgerItem): { id: string; name: string }[] {
  return item.person ? [item.person, ...item.others] : item.others
}

/** Who an item is with, in a row: the first name, and how many more. */
export function whoLabel(item: LedgerItem): string | null {
  const [first, ...rest] = withWhom(item)
  if (!first) return null
  return rest.length ? `${first.name} +${rest.length}` : first.name
}

/** Everyone the items name, alphabetically, with how many items each is on: the person filter. */
export function peopleIn(items: LedgerItem[]): { id: string; name: string; n: number }[] {
  const by = new Map<string, { id: string; name: string; n: number }>()
  for (const i of items) {
    for (const p of withWhom(i)) {
      const e = by.get(p.id) ?? { id: p.id, name: p.name, n: 0 }
      e.n++
      by.set(p.id, e)
    }
  }
  return [...by.values()].sort((a, b) => a.name.localeCompare(b.name))
}

function dayNumber(day: string): number {
  return Math.round(Date.parse(`${day}T12:00:00Z`) / 86_400_000)
}

export interface DueLabel {
  text: string
  overdue: boolean
  soon: boolean
}

/** "Thu 23 Oct", with the year when it is not today's. */
export function dayLabel(day: string, today: string): string {
  const d = new Date(`${day}T12:00:00Z`)
  const text = d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: day.slice(0, 4) === today.slice(0, 4) ? undefined : 'numeric',
    timeZone: 'UTC'
  })
  return text.replace(',', '')
}

/**
 * What the row and the pane show by an item: a to-do's due date against
 * today, or a decision's date — a plain date, never late and never soon.
 */
export function itemDateLabel(item: LedgerItem, today: string): DueLabel | null {
  if (item.kind !== 'decision') return dueLabel(item.dueDate, today)
  return item.date ? { text: dayLabel(item.date, today), overdue: false, soon: false } : null
}

/** A closed decision is archived; a to-do is done (or decided), dropped (or moot) or stale. */
export function statusLabel(item: {
  kind: ItemKind
  status: ItemStatus
  decidedBy?: unknown
}): string {
  if (item.kind === 'decision' && item.status !== 'open') return 'archived'
  if (item.decidedBy && item.status === 'done') return 'decided'
  if (item.decidedBy && item.status === 'dropped') return 'moot'
  return item.status
}

/** "2 days late", "today", "tomorrow", "in 4 days", else "Thu 23 Oct". */
export function dueLabel(due: string | null, today: string): DueLabel | null {
  if (!due) return null
  const diff = dayNumber(due) - dayNumber(today)
  if (diff < 0) {
    return { text: diff === -1 ? '1 day late' : `${-diff} days late`, overdue: true, soon: false }
  }
  if (diff === 0) return { text: 'today', overdue: false, soon: true }
  if (diff === 1) return { text: 'tomorrow', overdue: false, soon: true }
  if (diff < 7) return { text: `in ${diff} days`, overdue: false, soon: true }
  return { text: dayLabel(due, today), overdue: false, soon: false }
}
